import { useState } from 'react';
import { ArrowLeft, Loader2, RefreshCw, Wand2, Download, ExternalLink, AlertTriangle, FolderUp } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { apiPost } from '../../lib/api';
import { StatusPill, FormatBadge, isInFlight } from './StatusPill';

const API = process.env.REACT_APP_BACKEND_URL;

const Notice = ({ tone, title, body, testId }) => (
  <Card className={tone === 'red' ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'} data-testid={testId}>
    <CardContent className="pt-6 flex gap-3">
      <AlertTriangle size={18} className={tone === 'red' ? 'text-red-600 mt-0.5' : 'text-amber-700 mt-0.5'} />
      <div className="min-w-0">
        <p className={`font-semibold ${tone === 'red' ? 'text-red-800' : 'text-amber-900'}`}>{title}</p>
        <p className={`text-sm mt-1 break-words ${tone === 'red' ? 'text-red-700' : 'text-amber-800'}`}>{body}</p>
      </div>
    </CardContent>
  </Card>
);

const useAction = (fn, ok) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await fn();
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Request failed');
      ok(data);
    } catch (e) {
      toast.error(e.message || 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
};

const DetailActions = ({ deck, onRefresh }) => {
  const [downloading, download] = useAction(
    () => apiPost(`/api/presentations/${deck.id}/download-link`, {}),
    (d) => { window.location.href = `${API}${d.download_url}`; },
  );
  const [regenerating, regenerate] = useAction(
    () => apiPost(`/api/presentations/${deck.id}/regenerate`, {}),
    () => { toast.success('Regeneration started'); onRefresh(); },
  );
  const [saving, saveDrive] = useAction(
    () => apiPost(`/api/presentations/${deck.id}/save-to-drive`, {}),
    () => { toast.success('Saved to Drive'); onRefresh(); },
  );
  const ready = deck.status === 'Ready';
  return (
    <div className="flex flex-wrap gap-2 justify-end">
      <Button variant="outline" size="sm" onClick={onRefresh} data-testid="deck-detail-refresh"><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
      {ready && (
        <Button size="sm" onClick={download} disabled={downloading} data-testid="deck-download-file">
          {downloading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}
          Download .{deck.output_format}
        </Button>
      )}
      {ready && deck.drive_web_view_link && (
        <a href={deck.drive_web_view_link} target="_blank" rel="noopener noreferrer" data-testid="deck-open-drive"
          className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md border border-border-subtle hover:border-brand/40 transition-colors">
          <ExternalLink size={14} /> Open in Drive
        </a>
      )}
      {ready && !deck.drive_web_view_link && (
        <Button variant="outline" size="sm" onClick={saveDrive} disabled={saving} data-testid="deck-save-drive">
          {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FolderUp size={14} className="mr-1.5" />} Save to Drive
        </Button>
      )}
      {!isInFlight(deck) && (
        <Button variant="outline" size="sm" onClick={regenerate} disabled={regenerating} data-testid="deck-detail-regenerate">
          {regenerating ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Wand2 size={14} className="mr-1.5" />} Regenerate
        </Button>
      )}
    </div>
  );
};

const InputsPanel = ({ deck }) => (
  <Card data-testid="deck-inputs">
    <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
      {[['Outline', deck.outline], ['Data', deck.data_summary]].map(([label, val]) => (
        <div key={label}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-1">{label}</p>
          <pre className="whitespace-pre-wrap font-sans text-text-secondary max-h-64 overflow-y-auto">{val || '—'}</pre>
        </div>
      ))}
    </CardContent>
  </Card>
);

export const DeliverableDetail = ({ deck, onBack, onRefresh }) => (
  <div className="space-y-6" data-testid="deck-detail-view">
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <button onClick={onBack} className="text-sm text-text-muted hover:text-text-primary inline-flex items-center gap-1.5 mb-2" data-testid="deck-detail-back">
          <ArrowLeft size={14} /> Back to Skill Studio
        </button>
        <h1 className="text-3xl font-bold tracking-tight text-text-primary" data-testid="deck-detail-title">{deck.title}</h1>
        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-text-secondary">
          <span className="font-medium">{deck.client_label || deck.client_name}</span>
          <span className="text-text-muted">·</span><span>{deck.period}</span>
          <FormatBadge format={deck.output_format} /><StatusPill status={deck.status} />
        </div>
      </div>
      <DetailActions deck={deck} onRefresh={onRefresh} />
    </div>
    {deck.status === 'Failed' && deck.error && <Notice tone="red" title="Generation failed" body={deck.error} testId="deck-error" />}
    {deck.drive_error && <Notice tone="amber" title="Drive copy failed — the file is still downloadable" body={deck.drive_error} testId="deck-drive-error" />}
    {isInFlight(deck) && (
      <Card data-testid="deck-generating"><CardContent className="pt-6 text-center">
        <Loader2 className="mx-auto animate-spin text-brand mb-3" size={28} />
        <p className="text-text-primary font-medium">Claude is building your file with the BCM skill…</p>
        <p className="text-sm text-text-muted mt-1">This usually takes 2–6 minutes. You can leave this page; it keeps going.</p>
      </CardContent></Card>
    )}
    {deck.status === 'Ready' && deck.summary && (
      <Card data-testid="deck-summary"><CardContent className="pt-6">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-1">What Claude built</p>
        <p className="text-sm text-text-primary leading-relaxed">{deck.summary}</p>
        <p className="text-xs text-text-muted mt-3">{deck.file_name} · {Math.round((deck.file_size || 0) / 1024)} KB · {deck.model} · skill v{deck.skill_version}</p>
      </CardContent></Card>
    )}
    <InputsPanel deck={deck} />
  </div>
);
